import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEa, useGasUnit } from "@/components/energy-analytics/analytics/EaContext";
import { axisFmt } from "@/components/energy-analytics/analytics/EaConsumptionCharts";
import { EaMultiYearCard } from "@/components/energy-analytics/analytics/EaMultiYearCard";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaGasUnitToggle, EaPageHeader } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { EaYoyCard } from "@/components/energy-analytics/analytics/EaYoyCard";
import { COLORS, MONTHS_IT, fmtQty, gasLabel } from "@/components/energy-analytics/analytics/eaFormat";
import { PageState } from "./EaPowerOverview";

const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };
export const GAS_PALETTE = ["#3285FD", "#8B32FB", "#F59E0B", "#10B981", "#EF4444", "#06B6D4", "#A855F7", "#64748B"];

function YearChart({ monthly }) {
  const g = useGasUnit();
  const data = monthly.map((m) => ({ ...m, label: MONTHS_IT[m.month - 1], fc: m.status === "FORECAST" }));
  return (
    <div className="h-80" data-testid="ea-gas-cons-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" {...axis} />
          <YAxis {...axis} width={80} tickFormatter={axisFmt} label={{ value: g.u, angle: -90, position: "insideLeft", fontSize: 11, fill: "#64748B" }} />
          <Tooltip formatter={(v, n) => [fmtQty(v, g.d, g.u), n]} labelFormatter={(l, p) => `${l}${p?.[0]?.payload?.fc ? " · previsionale" : " · consuntivo"}`} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="total" name="Consumo effettivo" fill={COLORS.gas} radius={[3, 3, 0, 0]}>
            {data.map((r) => <Cell key={r.month} fillOpacity={r.fc ? 0.3 : 1} />)}
          </Bar>
          <Line dataKey="contractual" name="Consumo contrattuale" stroke="#071B4D" strokeDasharray="5 3" strokeWidth={2} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function PointsChart({ perPoint }) {
  const g = useGasUnit();
  const data = MONTHS_IT.map((label, i) => Object.fromEntries([["label", label], ...perPoint.map((p) => [p.code, g.q(p.monthly[i])])]));
  return (
    <div className="h-72" data-testid="ea-gas-cons-points-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" {...axis} />
          <YAxis {...axis} width={80} tickFormatter={axisFmt} />
          <Tooltip formatter={(v, n) => [fmtQty(v, g.d, g.u), n]} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {perPoint.map((p, i) => <Bar key={p.code} dataKey={p.code} name={gasLabel(p)} stackId="p" fill={GAS_PALETTE[i % GAS_PALETTE.length]} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function EaGasConsumption() {
  const codes = useSelectedCodes("GAS");
  const [cy, setCy] = useState(null);
  const { filters: { year: selYear } } = useEa();
  useEffect(() => setCy(null), [selYear]);
  const { data: d, loading, error } = useEaData("/ea/gas/consumption", { codes, compare_year: cy || "" });
  const g = useGasUnit();
  const monthly = (d?.monthly || []).map((m) => ({ ...m, total: g.q(m.total), contractual: g.q(m.contractual) }));
  const header = <EaPageHeader title="Consumi Gas" subtitle="Consumi mensili effettivi e contrattuali, ripartizione per utenza e confronto con l'anno precedente." units={<EaGasUnitToggle />} right={<EaPointPicker commodity="GAS" />} />;
  return (
    <PageState header={header} loading={loading} error={error} data={d} testid="ea-gas-consumption">
      {d && <>
        <EaChartCard title="Consumo mensile" subtitle="Barre piene = consuntivo, tenui = previsionale · linea tratteggiata = consumo contrattuale" testid="ea-gas-cons-card"
          right={<EaExportButton filename={`gas_consumi_${d.year}`} commodity="GAS" year={d.year} testid="ea-gas-cons-export"
            columns={[{ key: "month", label: "Mese" }, { key: "status", label: "Stato" }, { key: "total", label: `Effettivo ${g.u}` }, { key: "contractual", label: `Contrattuale ${g.u}` }, { key: "pcs_kj", label: "PCS kJ/Smc" }]} rows={monthly} />}>
          <YearChart monthly={monthly} />
        </EaChartCard>
        {d.per_point.length > 1 && (
          <EaChartCard title="Consumo per utenza" subtitle={`Barre impilate per utenza (${g.u})`} testid="ea-gas-cons-points-card">
            <PointsChart perPoint={d.per_point} />
          </EaChartCard>
        )}
        <EaYoyCard yoy={d.yoy} title="Confronto anno su anno · consumi consuntivi" fmt={g.f} conv={g.q} unitLabel={g.u} tickDigits={g.u === "GWh" ? 2 : 0} color={COLORS.gas}
          commodity="GAS" filename={`gas_consumi_yoy_${d.year}`} testid="ea-gas-cons-yoy" compareYears={d.compare_years} onCompare={setCy} />
      <EaMultiYearCard commodity="GAS" codes={codes} color={COLORS.gas} testid="ea-gas-cons-multiyear" metrics={[{ key: "consumption", label: "Consumi", conv: g.q, fmt: (v) => fmtQty(v, g.d, g.u) }]} />
      </>}
    </PageState>
  );
}
